import { ColDef, GridReadyEvent } from "@ag-grid-community/core";
import { Component } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { MenuItem } from "primeng/api";
import { NotificationTemplateDialogComponent } from "./notification-template-dialog.component";
import { FileUploadHandlerEvent } from "primeng/fileupload";
import {
  Account,
  NotificationEvent,
  NotificationEventEnum,
  NotificationTemplate,
  NotificationType,
  NotificationTypeEnum,
} from "@/model/entities/entity-model";
import { AgFns, GridOptionsExt, ISortModel, RowSelectionMultipleCheckbox, RowSelectionSingle } from "@/shared/ag-fns";
import { DomainService } from "@/shared/domain.service";
import { DomainBaseComponent } from "@/shared/domain-base.component";
import { DbSaveService } from "@/shared/db-save.service";
import { DbQueryService } from "@/shared/db-query.service";
import { takeUntil } from "rxjs";
import { UtilFns } from "@/shared/util-fns";
import { StringFns } from "@/shared/string-fns";
import { EntityFns } from "@/shared/entity-fns";
import { Guid } from "guid-typescript";
import { EmailService } from "@/shared/email.service";

export interface TemplateType {
  id: TemplateTypeEnum;
  name: string;
}

export enum TemplateTypeEnum {
  FreeForm = 1,
  Event = 2,
  All = 3,
}

@Component({
  selector: "uni-notification-template-list",
  templateUrl: "./notification-template-list.component.html",
  standalone: false,
})
export class NotificationTemplateListComponent extends DomainBaseComponent {
  accountId!: string;
  account?: Account;
  allTemplates: NotificationTemplate[] = [];
  templates: NotificationTemplate[] = [];
  menuItems: MenuItem[] = [];
  gridOptions: GridOptionsExt = {};
  isAccountAdmin: boolean = false;
  templateType!: TemplateType;

  TemplateTypeEvent = { id: TemplateTypeEnum.Event, name: "Event based" } as TemplateType;
  TemplateTypeFreeForm = { id: TemplateTypeEnum.FreeForm, name: "Free form" } as TemplateType;
  TemplateTypeAll = { id: TemplateTypeEnum.All, name: "All" } as TemplateType;
  templateTypes = [this.TemplateTypeEvent, this.TemplateTypeFreeForm, this.TemplateTypeAll];

  private readonly EMAIL_WRAPPER_EVENTS = [
    NotificationEventEnum.EmailHeader,
    NotificationEventEnum.EmailFooter,
  ];

  constructor(baseService: DomainService, private route: ActivatedRoute, private emailService: EmailService) {
    super(baseService);

    this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext(): Promise<void> {
    this.accountId = this.route.snapshot.params["accountId"];
    // UtilFns.assertNonEmptyString(this.accountId, 'accountId');
    this.isAccountAdmin = this.domainService.authService.getUser()?.isAccountAdmin ?? false;

    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      onRowSelected: this.onRowSelected,
      onCellValueChanged: this.onCellValueChanged,
      rowSelection: this.isAccountAdmin ? RowSelectionMultipleCheckbox : RowSelectionSingle,
      getRowId: undefined, // couldn't refresh rows without setting this
    });

    this.templateType = this.templateTypes[0];
    if (this.accountId) {
      this.account = await this.dbQueryService.getAccount(+this.accountId);
    }

    await Promise.all([this.dbQueryService.getAll(NotificationEvent), this.dbQueryService.getAll(NotificationType), this.loadTemplates()]);

    AgFns.captureGridRouteParams(this.gridOptions, this.route, "id");

    this.isPageReady = true;
  }

  private async loadTemplates() {
    const all = await this.dbQueryService.getNotificationTemplates(this.accountId);
    this.allTemplates = all.filter(t => !this.EMAIL_WRAPPER_EVENTS.includes(t.notificationEventId));
    this.onTemplateTypeChange();
  }

  onTemplateTypeChange() {
    if (this.templateType.id == TemplateTypeEnum.All) {
      this.templates = this.allTemplates;
    } else {
      this.templates = this.allTemplates.filter(
        NotificationTemplate.getTemplateTypeFilter(this.templateType.id == TemplateTypeEnum.FreeForm)
      );
    }
    this.gridOptions.apiX?.setGridOption("rowData", this.templates);
  }

  /** Get the menu of template variables */
  // private loadMenuItems() {
  //   return this.templatesService.getTemplateMenuItems().then(x => { this.menuItems = x; });
  // }

  onGridReady(event: GridReadyEvent) {
    const colDefs: ColDef[] = [
      {
        ...AgFns.createButtonProps("", this.onEdit.bind(this), {
          label: "Edit",
          canDisplay: (item) => !!item.accountId || this.isAccountAdmin,
        }),
        ...AgFns.fixWidth(60),
      },
      {
        headerName: "Notification Event/Template Name",
        valueGetter: (params) => {
          const nt = params.data as NotificationTemplate;
          if (nt == null) return;
          return nt.templateName || nt.notificationEvent.description;
        },
        sortable: true,
        ...AgFns.fixWidth(200),
      },
      {
        headerName: "Template Type",
        valueGetter: (params) => {
          const nt = params.data as NotificationTemplate;
          if (nt == null) return;
          return nt.notificationEventId != NotificationEventEnum.FreeForm ? "Event based" : "Free form";
        },
        sortable: true,
        ...AgFns.fixWidth(140),
      },
      {
        headerName: "Notification Type",
        field: "notificationType.name",
        sortable: true,
        ...AgFns.fixWidth(200),
      },
      {
        headerName: "Active?",
        field: "isActive",
        cellDataType: "boolean",
        editable: () => this.isAccountAdmin,
        ...AgFns.fixWidth(120),
      },
      {
        headerName: "Template",
        field: "template",
        filter: "agTextColumnFilter",
        valueFormatter: (p) => StringFns.stripHtmlTags(p.value),
        ...AgFns.flexWidth(300),
      },
      { headerName: "isGlobal", valueGetter: (p) => p.data.accountId ?? "", hide: true },
      // ProxAgFns.getEntityDeleteColDef(this.onDelete.bind(this), { canDisplay: this.canDelete.bind(this)}),
      {
        // ...AgFns.createButtonProps('', this.download.bind(this), { icon: 'pi pi-download', }),
        ...AgFns.fixWidth(40),

        headerCheckboxSelection: true,
        hide: !this.isAccountAdmin,
      },
    ];

    const sortModel: ISortModel = [
      { colId: "isGlobal", sort: "desc" },
      { colId: "notificationEvent.description", sort: "asc" },
      { colId: "notificationType.name", sort: "asc" },
    ];

    AgFns.initGrid(event, colDefs, sortModel);
  }

  getIsGlobalIcon(row: NotificationTemplate) {
    if (!row.accountId) {
      return "pi pi-check";
    } else {
      return "";
    }
  }

  onRowSelected() {}

  async onCellValueChanged(event: any) {
    if (event.colDef.field === "isActive") {
      const template = event.data as NotificationTemplate;
      try {
        await this.dbSaveService.saveSelectedChanges([template]);
        const statusText = template.isActive ? "enabled" : "disabled";
        const templateName = template.templateName || template.notificationEvent?.description || "Template";
        this.toastr.success(`${templateName} ${statusText}.`);
      } catch (error) {
        this.toastr.error("Failed to save template status.");
        template.entityAspect.rejectChanges();
        this.gridOptions.apiX?.refreshCells();
      }
    }
  }

  canAdd() {
    return this.templateType.id != TemplateTypeEnum.All;
  }

  async onEdit(notificationTemplate: NotificationTemplate) {
    const dlg = this.domainService.pngDialogService;
    const ok = await NotificationTemplateDialogComponent.openDialog(dlg, { notificationTemplate, isEditable: true });
    if (ok) {
      await this.dbSaveService.saveSelectedChanges([notificationTemplate]);
      this.gridOptions.apiX?.refreshCells();
    } else {
      notificationTemplate.entityAspect.rejectChanges();
      this.gridOptions.apiX?.refreshCells();
    }
  }

  async onAdd() {
    const template = this.dbSaveService.createEntity(NotificationTemplate, {
      id: Guid.create().toString(),
      accountId: this.isAccountAdmin ? undefined : this.accountId,
    });
    if (this.templateType.id == TemplateTypeEnum.FreeForm) {
      template.notificationEventId = NotificationEventEnum.FreeForm;
    }
    await this.editNew(template);
  }

  private async editNew(notificationTemplate: NotificationTemplate) {
    const dlg = this.domainService.pngDialogService;
    const ok = await NotificationTemplateDialogComponent.openDialog(dlg, { notificationTemplate, isNew: true });

    if (ok) {
      await this.dbSaveService.saveSelectedChanges([notificationTemplate]);
      await this.loadTemplates();
    } else {
      EntityFns.deleteOrDetach(notificationTemplate.entityAspect);
    }
    this.gridOptions.apiX?.redrawRows();
  }

  canCopy() {
    const rows = this.gridOptions.apiX?.getSelectedRows();
    return rows && rows.length == 1;
  }

  async onCopy() {
    const rows = this.gridOptions.apiX?.getSelectedRows();
    if (rows && rows.length) {
      const old = rows[0] as NotificationTemplate;
      const props = EntityFns.cloneStruct(old, ["id", "rowVersion"]) as any;
      props.accountId = this.accountId;
      props.id = Guid.create().toString();
      // TODO - change event or type so we don't create duplicate template
      const template = this.dbSaveService.createEntity(NotificationTemplate, props);
      template.notificationTypeId = <any>undefined;
      (template.accountId = this.isAccountAdmin ? undefined : this.accountId), await this.editNew(template);
    }
  }

  canDelete(notificationTemplate: NotificationTemplate) {
    return notificationTemplate.accountId != null;
  }

  async onDelete(notificationTemplate: NotificationTemplate) {
    // if (row.isPreset) {
    //   this.toastr.warning(
    //     'Cannot delete a preset communication',
    //     'Unable to Proceed'
    //   );
    // }
    const name = notificationTemplate.templateName ?? NotificationEventEnum[notificationTemplate.notificationEventId!];
    const ok = await this.domainService.dialogService.yesNoDialog(
      "Delete Template",
      'Are you sure you want to delete template "' + name + '"?',
      'Ok',
      'Cancel',
    );
    if (ok) {
      EntityFns.deleteOrDetach(notificationTemplate.entityAspect);
      await this.dbSaveService.saveSelectedChanges([notificationTemplate]);
      await this.loadTemplates();
    }
  }

  /** Download template as json */
  // download(notificationTemplate: NotificationTemplate) {
  //   const json = JSON.stringify(EntityFns.cloneStruct(notificationTemplate, ['id','rowVersion']));
  //   UtilFns.downloadFile(json, 'application/json', 'template.json');
  // }

  hasSelectedRows(): boolean {
    const selected = this.gridOptions.apiX?.getSelectedRows();
    return !!(selected && selected.length);
  }

  /** Download selected templates as json */
  downloadSelected() {
    const selected = this.gridOptions.apiX?.getSelectedRows();
    if (!selected || !selected.length) {
      return;
    }
    const structs = selected.map((x) => EntityFns.cloneStruct(x, ["id", "rowVersion"]));
    const json = JSON.stringify(structs);
    UtilFns.downloadFile(json, "application/json", "templates.json");
  }

  /** Parse uploaded json, create and save entities */
  async uploadHander(event: FileUploadHandlerEvent) {
    const file = event.files.shift();
    if (!file) {
      return;
    }
    const data = await file.text();
    let config = JSON.parse(data);
    if (!Array.isArray(config)) {
      config = [config];
    }

    for (const c of config) {
      if (c.accountId) {
        // if it was an account-specific template, assign it to this account.
        c.accountId = this.accountId;
      }
      if (!this.isDuplicate(c)) {
        const ent = this.dbSaveService.createEntity(NotificationTemplate, c);
        this.templates.push(ent);
      }
    }
    await this.dbSaveService.saveChanges();
    this.gridOptions.apiX?.setGridOption("rowData", this.templates);
  }

  private isDuplicate(c: Partial<NotificationTemplate>) {
    if (
      this.templates.some(
        (x) => x.accountId == c.accountId && x.notificationEventId == c.notificationEventId && x.notificationTypeId == c.notificationTypeId
      )
    ) {
      this.toastr.warning(
        `Omitting template for ${NotificationEventEnum[c.notificationEventId!]} ${
          NotificationTypeEnum[c.notificationTypeId!]
        } because one already exists`
      );
      return true;
    }
    return false;
  }
}
